import StarRating from '@/features/leads/StarRating'
import { ModalShell, ModalHead, ModalSection, ModalLabel, ModalFoot } from '@/ui/Modal'
import { dayKey, nextBusinessDay } from './businessDays'
import { OUTCOMES, RATING_TIER, RATING_REASONS, requiresExplicitNextDate, type OutcomeKind } from '@/shared/domain'
import type { Lead } from '@/shared/types'
import { Input, Textarea } from '@/ui'

// Append a reason chip's text to the comment, deduped, with a separator.
function appendReason(current: string, phrase: string): string {
  const trimmed = current.trim()
  if (trimmed.toLowerCase().includes(phrase.toLowerCase())) return current
  if (!trimmed) return phrase
  return trimmed.endsWith('.') || trimmed.endsWith('·') ? `${trimmed} ${phrase}` : `${trimmed} · ${phrase}`
}

export interface OutcomeDraft {
  kind: OutcomeKind
  rating: number
  comment: string
  nextDate: string         // ISO yyyy-mm-dd, '' if not asked
  // Optional HH:MM in 24-hour local time. Empty when the BDR hasn't opted
  // into a specific-time callback — the lead surfaces silently when the
  // date passes. Non-empty when the BDR committed to a moment; that drives
  // the in-app notification poller via `isCallbackAlert=true` on submit.
  nextTime: string
  nextNote: string
  // Reached-only: prospect's planned relocation date (ISO yyyy-mm-dd, '' if
  // unset) and whether they're moving to an office hotel (kontorshotell).
  moveDate: string
  officeHotel: boolean
  likelyBooking: boolean
}

interface Props {
  lead: Lead
  outcome: OutcomeDraft
  setOutcome: (next: OutcomeDraft | ((prev: OutcomeDraft) => OutcomeDraft)) => void
  onCancel: () => void
  onSubmit: () => void
  /** Fires for Escape specifically, before onCancel — see ModalShell. */
  onEscapeKeyDown?: () => void
}

export default function OutcomeSheet({ lead, outcome, setOutcome, onCancel, onSubmit, onEscapeKeyDown }: Props) {
  const def = OUTCOMES[outcome.kind]
  const needsDate = requiresExplicitNextDate(outcome.kind) && !outcome.nextDate

  // Two preset rows for non-booked outcomes: short-term follow-up vs. long
  // park-it-for-later. BDRs frequently need to defer a lead 6+ months when a
  // contact asks to be reached "next year" — arrow-keying that distance on
  // the date input is friction the chips remove.
  const shortPresets = outcome.kind === 'booked'
    ? [
        { label: 'Imorgon', d: 1 },
        { label: 'Om 3 dagar', d: 3 },
        { label: 'Om 1 vecka', d: 7 },
      ]
    : [
        { label: 'Imorgon', d: 1 },
        { label: 'Om 3 dagar', d: 3 },
        { label: 'Om 1 vecka', d: 7 },
        { label: 'Om 2 veckor', d: 14 },
      ]
  const longPresets = outcome.kind === 'booked'
    ? []
    : [
        { label: '3 månader', d: 91 },
        { label: '6 månader', d: 182 },
        { label: '12 månader', d: 365 },
        { label: '2 år', d: 730 },
      ]

  const setNextIn = (days: number) => {
    const d = new Date()
    d.setDate(d.getDate() + days)
    // Non-booked outcomes nudge the chip date forward past Sat/Sun so the
    // BDR's planned follow-up never lands on a weekend (RADAR-SE-021).
    // Booked outcomes carry an actual meeting time and stay exact — a
    // meeting on a weekend is the BDR's deliberate choice, not friction.
    const adjusted = outcome.kind === 'booked' ? d : nextBusinessDay(d)
    setOutcome(o => ({ ...o, nextDate: dayKey(adjusted) }))
  }

  return (
    <ModalShell onClose={onCancel} onEscapeKeyDown={onEscapeKeyDown}>
      <ModalHead
        eyebrow="Steg 2 · logga utfall"
        title={<>{def.label} · {lead.company}</>}
        onClose={onCancel}
      />

      {def.askRating && (
        <ModalSection>
          <ModalLabel>
            {outcome.kind === 'notfit' ? 'Hur starkt var signalen?' : 'Hur het är den här leaden?'}
          </ModalLabel>
          <div className="flex items-center gap-4">
            <StarRating
              value={outcome.rating}
              onChange={r => setOutcome(o => ({ ...o, rating: r }))}
              size={36}
            />
            <span className="text-sm font-semibold text-ink-1000">
              {RATING_TIER[outcome.rating] || 'Klicka på en stjärna för att betygsätta'}
            </span>
          </div>
        </ModalSection>
      )}

      <ModalSection>
        <ModalLabel>
          {outcome.kind === 'notfit' ? 'Varför inte aktuell?' : 'Lägg till en kommentar'}
        </ModalLabel>
        {(outcome.kind === 'notfit' || outcome.kind === 'booked') && (
          <div className="flex flex-wrap gap-1 mb-2">
            {(outcome.kind === 'notfit' ? RATING_REASONS.notfit : RATING_REASONS.positive).map(r => (
              <button
                key={r}
                type="button"
                className="px-2 py-1 text-[11px] text-ink-500 bg-transparent border border-ink-200 rounded-[2px] cursor-pointer transition-colors hover:bg-ink-50 hover:text-ink-1000 active:bg-ink-100"
                onClick={() =>
                  setOutcome(o => ({ ...o, comment: appendReason(o.comment, r) }))
                }
              >
                + {r}
              </button>
            ))}
          </div>
        )}
        <Textarea
          rows={3}
          placeholder={
            outcome.kind === 'notfit'
              ? 'Fel storlek, fel bransch, ingen budget…'
              : outcome.kind === 'voicemail'
                ? 'Något att komma ihåg till nästa försök?'
                : outcome.kind === 'customer'
                  ? 'Valfritt — redan kund / aktiv prospect sedan…'
                  : outcome.kind === 'bad_contact'
                    ? 'Numret nedkopplat / fel person / kontakten har slutat…'
                    : outcome.kind === 'email_sent'
                      ? 'Vad handlade mejlet om? (ämne, viktigaste fråga…)'
                      : 'Vad var känslan? Köpsignaler, invändningar, nästa steg…'
          }
          value={outcome.comment}
          onChange={e => setOutcome(o => ({ ...o, comment: e.target.value }))}
          autoFocus
        />
      </ModalSection>

      {def.askNext && (
        <ModalSection>
          <ModalLabel>
            {outcome.kind === 'booked' ? 'När är mötet?' : 'När ska vi ta kontakt igen?'}
          </ModalLabel>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <input
              type="date"
              className={`px-3 py-2 text-sm font-mono text-ink-1000 bg-white border rounded-[2px] focus:outline-none focus:border-ink-1000 ${needsDate ? 'border-terracotta' : 'border-ink-200'}`}
              value={outcome.nextDate}
              onChange={e => setOutcome(o => ({ ...o, nextDate: e.target.value }))}
            />
            {/* Specific-time toggle. For non-booked outcomes this also drives
                the callback-alert flow (RADAR-SE-022): when ON, is_callback_alert
                is set on submit so the in-app poller pings at that moment.
                Booked gets the same toggle so a meeting can carry a time, but
                never sets the alert — a meeting reminder is a different thing
                from a promised callback (see useOutcomeFlow). */}
            <label className="inline-flex items-center gap-1.5 text-[11px] text-ink-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!outcome.nextTime}
                onChange={e => setOutcome(o => ({ ...o, nextTime: e.target.checked ? (o.nextTime || '10:00') : '' }))}
                className="accent-brand"
              />
              Specifik tid
            </label>
            {outcome.nextTime && (
              <input
                type="time"
                className="px-3 py-2 text-sm font-mono text-ink-1000 bg-white border border-ink-200 rounded-[2px] focus:outline-none focus:border-ink-1000"
                value={outcome.nextTime}
                onChange={e => setOutcome(o => ({ ...o, nextTime: e.target.value }))}
              />
            )}
            <div className="flex flex-wrap gap-1">
              {shortPresets.map(p => (
                <button
                  key={p.label}
                  type="button"
                  className="px-2.5 py-1 text-[11px] text-ink-700 bg-white border border-ink-200 rounded-[2px] cursor-pointer transition-colors hover:border-ink-1000 hover:text-ink-1000"
                  onClick={() => setNextIn(p.d)}
                >
                  {p.label}
                </button>
              ))}
              {longPresets.length > 0 && (
                <span className="mx-1 self-center text-ink-300" aria-hidden>·</span>
              )}
              {longPresets.map(p => (
                <button
                  key={p.label}
                  type="button"
                  className="px-2.5 py-1 text-[11px] text-ink-500 bg-white border border-ink-200 rounded-[2px] cursor-pointer transition-colors hover:border-ink-1000 hover:text-ink-1000"
                  onClick={() => setNextIn(p.d)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {needsDate && (
            <p className="text-[11px] text-terracotta mt-0 mb-2">Välj ett datum för att spara det här utfallet.</p>
          )}
          <Input
            type="text"
            className="w-full"
            placeholder={
              outcome.kind === 'booked'
                ? 'Mötesanteckning (t.ex. "Teams-samtal, introduktion")'
                : outcome.kind === 'email_sent'
                  ? 'Vad som ska följas upp (t.ex. "följ upp prissvar")'
                  : 'Anledning / vad som ska förberedas (valfritt)'
            }
            value={outcome.nextNote}
            onChange={e => setOutcome(o => ({ ...o, nextNote: e.target.value }))}
          />
        </ModalSection>
      )}

      {outcome.kind === 'reached' && (
        <ModalSection>
          <ModalLabel>Pipeline</ModalLabel>
          <label className="inline-flex items-center gap-1.5 text-[12px] text-ink-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={outcome.likelyBooking}
              onChange={e => setOutcome(o => ({ ...o, likelyBooking: e.target.checked }))}
              className="accent-brand"
            />
            Sannolikt bok
          </label>
        </ModalSection>
      )}

      {outcome.kind === 'reached' && (
        <ModalSection>
          <ModalLabel>Planerad flytt (valfritt)</ModalLabel>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="date"
              className="px-3 py-2 text-sm font-mono text-ink-1000 bg-white border border-ink-200 rounded-[2px] focus:outline-none focus:border-ink-1000"
              value={outcome.moveDate}
              onChange={e => setOutcome(o => ({ ...o, moveDate: e.target.value }))}
            />
            {outcome.moveDate && (
              <button
                type="button"
                className="text-[11px] text-ink-500 bg-transparent border-0 cursor-pointer hover:text-ink-1000"
                onClick={() => setOutcome(o => ({ ...o, moveDate: '' }))}
              >
                Rensa
              </button>
            )}
            <label className="inline-flex items-center gap-1.5 text-[12px] text-ink-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={outcome.officeHotel}
                onChange={e => setOutcome(o => ({ ...o, officeHotel: e.target.checked }))}
                className="accent-brand"
              />
              Kontorshotell
            </label>
          </div>
        </ModalSection>
      )}

      <ModalFoot
        onCancel={onCancel}
        onSave={onSubmit}
        saveDisabled={(def.askRating && !outcome.rating) || needsDate}
        saveLabel="Spara & nästa lead"
      />
    </ModalShell>
  )
}
